import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

/**
 * Terms of Service Page for Spawnry
 * Covers all tools, services, data handling, and liability
 */
const TermsOfService = () => {
  const sectionStyle = {
    marginBottom: '2rem'
  };

  const headingStyle = {
    marginBottom: '1rem',
    color: '#1a1a2e',
    fontWeight: '600'
  };

  const subheadingStyle = {
    fontSize: '1.1rem',
    marginTop: '1.5rem',
    color: '#1a1a2e',
    fontWeight: '600'
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | Spawnry</title>
        <meta name="description" content="Terms of Service for Spawnry - Article to Media transformation services" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Dark Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 4rem'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8} className="text-center">
              <h1 style={{
                color: 'white',
                fontWeight: '700',
                fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
                marginBottom: '0.75rem'
              }}>
                Terms of Service
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '1.1rem',
                margin: 0
              }}>
                Effective Date: January 24, 2026
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Content Card */}
      <Container style={{ marginTop: '-2rem', paddingBottom: '3rem' }}>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2.5rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
            }}>
              <section style={sectionStyle}>
                <h2 style={headingStyle}>1. About Spawnry</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  Welcome to Spawnry! Spawnry is a service of Teq Crew, LLC that transforms article
                  URLs into multiple media formats including audio summaries, social media posts with
                  AI-generated images, and summary content.
                </p>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  If you have any concerns or issues, please reach out through our{' '}
                  <Link to="/contact" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>contact form</Link>.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>2. Acceptance of Terms</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  By accessing or using spawnry.ai (the "Website") and any services, tools, or features
                  offered through it (collectively, the "Services"), you agree to be bound by these
                  Terms of Service ("Terms"). If you do not agree to these Terms, please do not use
                  the Services.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>3. Description of Services</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>The Services include:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>Article to Media Tool:</strong> Transforms article URLs into audio summaries, social media posts with AI-generated images, and summary content</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Contact Forms:</strong> Methods to reach us with inquiries</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Future Tools and Features:</strong> Additional tools and services we may introduce</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>4. User Requirements</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>To use our Services, you must:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Provide a valid email address</li>
                  <li>Only submit URLs for content you have the right to process</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>5. Access Codes</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>Some Services may offer access codes for enhanced features. If you receive an access code:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>You are responsible for maintaining its confidentiality</li>
                  <li>You agree not to share, sell, or transfer your access code</li>
                  <li>You are responsible for all activity under your access code</li>
                  <li>We reserve the right to revoke access codes at any time</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>6. Acceptable Use</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>You agree not to:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Use the Services to process content you do not have the right to use</li>
                  <li>Submit URLs to paywalled, private, or restricted content without authorization</li>
                  <li>Attempt to circumvent usage limits or security measures</li>
                  <li>Use automated systems or bots to access the Services without permission</li>
                  <li>Interfere with or disrupt the Services</li>
                  <li>Generate illegal, harmful, or defamatory content</li>
                  <li>Reverse engineer or extract source code from the Services</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>7. Content and Intellectual Property</h2>
                <h3 style={subheadingStyle}>Your Content</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  You retain ownership of URLs you submit. By using the Services, you grant us a
                  limited license to process your content solely for providing the Services.
                </p>

                <h3 style={subheadingStyle}>Generated Content</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  Content generated by our tools is provided for your use. You are responsible for
                  ensuring your use complies with applicable laws and respects original content creators' rights.
                </p>

                <h3 style={subheadingStyle}>Our Content</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  The Website, its original content, features, and functionality are owned by Teq Crew, LLC
                  and protected by intellectual property laws.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>8. Data Handling and Privacy</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  Your use of the Services is governed by our <Link to="/privacy-policy" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>Privacy Policy</Link>.
                </p>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  <strong style={{ color: '#1a1a2e' }}>Public Accessibility:</strong> Results generated by our tools are accessible
                  via unique URLs and may be indexed by search engines. Do not process sensitive or
                  private information if you do not want it to be publicly accessible.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>9. Usage Limits</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>The Services may impose usage limits including:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Daily processing caps that reset at midnight Central Time (America/Chicago)</li>
                  <li>Rate limiting to prevent abuse</li>
                  <li>Domain restrictions for supported content sources</li>
                </ul>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>We reserve the right to modify usage limits at any time.</p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>10. Third-Party Services</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>The Services integrate with third-party services including:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>OpenAI:</strong> For AI-powered content generation</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Amazon Web Services (AWS):</strong> For file storage and hosting</li>
                  <li><strong style={{ color: '#1a1a2e' }}>SendGrid:</strong> For email delivery</li>
                </ul>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>Your use is also subject to these providers' terms and policies.</p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>11. Disclaimer of Warranties</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7', background: 'rgba(99, 102, 241, 0.05)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
                  WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE,
                  OR THAT GENERATED CONTENT WILL BE ACCURATE OR SUITABLE FOR ANY PURPOSE.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>12. Limitation of Liability</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7', background: 'rgba(99, 102, 241, 0.05)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT,
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY SHALL
                  NOT EXCEED THE AMOUNT YOU PAID US, IF ANY, IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>13. Indemnification</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  You agree to indemnify and hold us harmless from any claims arising out of your use
                  of the Services, your violation of these Terms, or content you submit.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>14. Termination</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  We reserve the right to terminate or suspend your access to the Services at any time,
                  without prior notice, for any reason including breach of these Terms.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>15. Changes to Terms</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  We may modify these Terms at any time. Changes will be posted on this page with an
                  updated "Effective Date." Continued use constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>16. Governing Law</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  These Terms shall be governed by and construed in accordance with the laws of the
                  State of Texas, United States.
                </p>
              </section>

              <section style={{ marginBottom: '1rem' }}>
                <h2 style={headingStyle}>17. Contact</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7', marginBottom: 0 }}>
                  If you have questions about these Terms, please reach out through the{' '}
                  <Link to="/contact" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>contact form</Link>.
                </p>
              </section>

            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default TermsOfService;
